import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { useSnackbar } from "notistack";
import {
  useUpdateUsageAnalytics,
  useUsageAnalytics,
} from "../../hooks/useUsageAnalytics";

interface TenantUsageAnalyticsSectionProps {
  tenantId: string;
}

/**
 * activity-event-model D10: per-tenant opt-out of the central pseudonymous
 * usage stream. A contract term, not a rollout flag — the switch is audited
 * (TENANT_USAGE_ANALYTICS_UPDATE) and takes effect at the next ingest.
 */
export function TenantUsageAnalyticsSection({
  tenantId,
}: TenantUsageAnalyticsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useUsageAnalytics(tenantId);
  const update = useUpdateUsageAnalytics(tenantId);
  const enabled = data?.usageAnalyticsEnabled ?? true;

  const handleToggle = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    update.mutate(
      { usageAnalyticsEnabled: checked },
      {
        onSuccess: () =>
          enqueueSnackbar(
            checked
              ? "Bruksanalyse slått på for tenanten"
              : "Bruksanalyse slått av — usage-hendelser forkastes ved mottak",
            { variant: "success" },
          ),
        onError: () =>
          enqueueSnackbar("Kunne ikke endre bruksanalyse", {
            variant: "error",
          }),
      },
    );
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <InsightsIcon color="primary" />
          <Typography variant="h6">Bruksanalyse</Typography>
          {data && (
            <Chip
              label={enabled ? "På" : "Av"}
              size="small"
              color={enabled ? "success" : "default"}
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pseudonym bruksstatistikk (hvilke funksjoner som brukes, aldri hvem)
          til den sentrale strømmen. Avtalevilkår per tenant: slå av når
          databehandleravtalen ikke dekker det. System-hendelser (offline-kø,
          synk) lagres uansett.
        </Typography>
        {isError ? (
          <Alert severity="error">Kunne ikke hente innstillingen.</Alert>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleToggle}
                disabled={isLoading || update.isPending}
                slotProps={{
                  input: { role: "switch", "aria-label": "Bruksanalyse aktivert" },
                }}
              />
            }
            label={enabled ? "Bruksanalyse er på" : "Bruksanalyse er av"}
          />
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          Endringen logges i plattformens revisjonslogg og gjelder fra neste
          mottatte hendelse.
        </Typography>
      </CardContent>
    </Card>
  );
}
